import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsKeyService {
  private cryptoKeyPair: CryptoKeyPair | null = null;
  // Static cache for decrypted key to allow synchronous access in module factories
  private static decryptedKeyCache: string | null = null;

  constructor() {}

  /**
   * Generates an RSA key pair for encryption/decryption
   */
  private async generateKeyPair(): Promise<CryptoKeyPair> {
    if (this.cryptoKeyPair) {
      return this.cryptoKeyPair;
    }

    try {
      this.cryptoKeyPair = await window.crypto.subtle.generateKey(
        {
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]), // 65537
          hash: 'SHA-256'
        },
        true, // extractable
        ['encrypt', 'decrypt']
      );
      return this.cryptoKeyPair;
    } catch (error) {
      throw new Error('Failed to generate RSA key pair: ' + error);
    }
  }

  /**
   * Exports the public key in RSAPublicKey format (RFC 3447) for .NET ImportRSAPublicKey
   * Extracts the RSAPublicKey from SPKI format
   */
  async exportPublicKey(publicKey: CryptoKey): Promise<string> {
    try {
      const spkiBuffer = await window.crypto.subtle.exportKey('spki', publicKey);
      const spkiArray = new Uint8Array(spkiBuffer);

      // Parse SPKI to extract RSAPublicKey
      // SPKI structure: SEQUENCE { AlgorithmIdentifier, BIT STRING { RSAPublicKey } }
      const rsaPublicKey = this.extractRSAPublicKeyFromSPKI(spkiArray);

      // Convert to base64 - create a new ArrayBuffer to avoid type issues
      const rsaKeyBuffer = new Uint8Array(rsaPublicKey).buffer;
      return this.arrayBufferToBase64(rsaKeyBuffer);
    } catch (error) {
      throw new Error('Failed to export public key: ' + error);
    }
  }

  /**
   * Extracts RSAPublicKey from SPKI format
   * SPKI: SEQUENCE { AlgorithmIdentifier, BIT STRING { RSAPublicKey } }
   * Returns the RSAPublicKey bytes (SEQUENCE { modulus INTEGER, publicExponent INTEGER })
   */
  private extractRSAPublicKeyFromSPKI(spki: Uint8Array): Uint8Array {
    let offset = 0;

    // Read outer SEQUENCE tag (0x30)
    if (spki[offset++] !== 0x30) {
      throw new Error('Invalid SPKI format: expected SEQUENCE');
    }

    // Read length of outer SEQUENCE
    const outerLength = this.readASN1Length(spki, offset);
    offset += this.getLengthBytesCount(spki[offset]);

    // Skip AlgorithmIdentifier SEQUENCE
    if (spki[offset++] !== 0x30) {
      throw new Error('Invalid SPKI format: expected AlgorithmIdentifier SEQUENCE');
    }
    const algIdLength = this.readASN1Length(spki, offset);
    offset += this.getLengthBytesCount(spki[offset]) + algIdLength;

    // Read BIT STRING tag (0x03)
    if (spki[offset++] !== 0x03) {
      throw new Error('Invalid SPKI format: expected BIT STRING');
    }

    // Read BIT STRING length
    const bitStringLength = this.readASN1Length(spki, offset);
    offset += this.getLengthBytesCount(spki[offset]);

    // Skip unused bits byte (usually 0x00)
    offset++;

    // Now we're at the RSAPublicKey SEQUENCE
    // Verify it's a SEQUENCE
    if (spki[offset] !== 0x30) {
      throw new Error('Invalid SPKI format: expected RSAPublicKey SEQUENCE');
    }

    // Read RSAPublicKey length
    const rsaKeyLength = this.readASN1Length(spki, offset + 1);
    const rsaKeyLengthBytes = this.getLengthBytesCount(spki[offset + 1]);

    // Extract the RSAPublicKey (including the SEQUENCE tag and length)
    const rsaKeyStart = offset;
    const rsaKeyEnd = offset + 1 + rsaKeyLengthBytes + rsaKeyLength;
    return spki.slice(rsaKeyStart, rsaKeyEnd);
  }

  /**
   * Reads ASN.1 length field
   */
  private readASN1Length(data: Uint8Array, offset: number): number {
    const firstByte = data[offset];

    // Short form (length < 128)
    if ((firstByte & 0x80) === 0) {
      return firstByte;
    }

    // Long form
    const lengthBytes = firstByte & 0x7F;
    if (lengthBytes === 0 || lengthBytes > 4) {
      throw new Error('Invalid ASN.1 length encoding');
    }

    let length = 0;
    for (let i = 0; i < lengthBytes; i++) {
      length = (length << 8) | data[offset + 1 + i];
    }

    return length;
  }

  /**
   * Gets the number of bytes used to encode the length
   */
  private getLengthBytesCount(firstByte: number): number {
    if ((firstByte & 0x80) === 0) {
      return 1; // Short form
    }
    return 1 + (firstByte & 0x7F); // Long form: 1 byte for length indicator + length bytes
  }

  /**
   * Converts ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Converts base64 string to ArrayBuffer
   * Handles padding and whitespace automatically
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    try {
      // Remove any whitespace and ensure proper padding
      const cleanedBase64 = base64.trim().replace(/\s/g, '');

      // Validate base64 format (basic check)
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanedBase64)) {
        throw new Error('Invalid base64 format');
      }

      const binaryString = window.atob(cleanedBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (error) {
      throw new Error(`Failed to decode base64: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Decrypts the encrypted data using the private key
   * Matches the encryption strategy: RSA-OAEP with SHA-256 hash
   * The encrypted data should be base64 encoded and may be chunked by the backend
   */
  async decryptData(encryptedData: string, privateKey: CryptoKey): Promise<string> {
    try {
      // Validate input
      if (!encryptedData || encryptedData.trim().length === 0) {
        throw new Error('Encrypted data is empty or invalid');
      }

      // Validate private key algorithm matches encryption strategy
      if (privateKey.algorithm.name !== 'RSA-OAEP') {
        throw new Error(`Invalid key algorithm: expected RSA-OAEP, got ${privateKey.algorithm.name}`);
      }

      // Validate hash algorithm in key (should be SHA-256)
      // For RSA-OAEP keys, the algorithm is RsaHashedKeyAlgorithm which includes hash
      const keyAlgorithm = privateKey.algorithm as RsaHashedKeyAlgorithm;
      if (keyAlgorithm.hash?.name !== 'SHA-256') {
        throw new Error(`Invalid key hash algorithm: expected SHA-256, got ${keyAlgorithm.hash?.name || 'undefined'}`);
      }

      // Decode base64 to ArrayBuffer (base64ToArrayBuffer handles trimming and whitespace)
      const encryptedBuffer = this.base64ToArrayBuffer(encryptedData);

      // Validate encrypted buffer size (RSA-OAEP with 2048-bit key produces 256-byte ciphertext)
      // The encrypted data should be exactly 256 bytes (or a multiple if chunked)
      if (encryptedBuffer.byteLength === 0) {
        throw new Error('Decoded encrypted data is empty');
      }

      // Decrypt using RSA-OAEP
      // Note: The hash algorithm (SHA-256) is part of the key's algorithm parameters
      // and is automatically used during decryption, matching the encryption strategy
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: 'RSA-OAEP'
        },
        privateKey,
        encryptedBuffer
      );

      // Decode the decrypted buffer to string
      const decryptedText = new TextDecoder('utf-8', { fatal: true }).decode(decryptedBuffer);

      // Validate decrypted text
      if (!decryptedText || decryptedText.trim().length === 0) {
        throw new Error('Decrypted data is empty');
      }

      return decryptedText;
    } catch (error: any) {
      // Provide more detailed error information
      if (error instanceof Error) {
        throw new Error(`Failed to decrypt data: ${error.message}`);
      }
      throw new Error(`Failed to decrypt data: ${String(error)}`);
    }
  }

  /**
   * Gets the public key as base64 string for API request
   * Generates key pair if not already generated
   * Returns RSAPublicKey format (RFC 3447) for .NET ImportRSAPublicKey
   */
  async getPublicKeyBase64(): Promise<string> {
    const keyPair = await this.generateKeyPair();
    return await this.exportPublicKey(keyPair.publicKey);
  }

  /**
   * Gets the private key for decryption
   * Generates key pair if not already generated
   */
  async getPrivateKey(): Promise<CryptoKey> {
    const keyPair = await this.generateKeyPair();
    return keyPair.privateKey;
  }

  /**
   * Caches the decrypted key for synchronous access in module factories
   */
  setDecryptedKeyCache(key: string): void {
    GoogleMapsKeyService.decryptedKeyCache = key;
  }

  /**
   * Gets the cached decrypted key (synchronous access)
   */
  getDecryptedKeyCache(): string | null {
    return GoogleMapsKeyService.decryptedKeyCache;
  }
}
